<script setup lang="ts">
// 2026-10-06 (Paolo, Japan mini charts spec §3.1): L&G Japan's top strip in the BGA-vs-NAS format — four tappable
// mini sparklines with % moves: JAPAN (live synth) · NIY (CME Nikkei since the Tokyo close, with the dashed 08:40 UK
// decision marker kept) · TOPIX (last 20 daily closes, the fund's benchmark) · FX (GBP/JPY today). Taps open the full
// charts. Same look/CSS as SparklineBar (BGA/JH). The SELL/BUY band lives on the Japan vs NIY chart, not here.
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type NiyBar, type JapanIntradayRow, type JapanTopixRow } from '../../api/client'
import { sparkPoints, sparkPathD } from '../sparkline'
import { findMarkerIndex } from './niyMarker'

const props = defineProps<{ anchorPrice: number | null }>()

const W = 80
const H = 18

const niy = ref<NiyBar[]>([])
const intraday = ref<JapanIntradayRow[]>([])
const topix = ref<JapanTopixRow[]>([])
const liveReturn = ref<number | null>(null)
let timer: ReturnType<typeof setInterval> | null = null

// Each feed is independent: a failed (or, in tests, absent) call leaves its tile on dashes, never the whole strip.
function safe<T>(f: () => Promise<T>): Promise<T | null> {
  try { return f().catch(() => null) } catch { return Promise.resolve(null) }
}

async function load() {
  const [n, i, t, live] = await Promise.all([
    safe(() => api.niyIntraday()),
    safe(() => api.japanChartsIntraday('1d')),
    safe(() => api.japanChartsTopix('1m')),
    safe(() => api.japanSynthNavLive()),
  ])
  niy.value = n ?? []
  intraday.value = i?.rows ?? []
  topix.value = t?.rows ?? []
  liveReturn.value = live?.returnFrac ?? null
}

onMounted(async () => {
  await load()
  timer = setInterval(load, 60_000)
})
onUnmounted(() => { if (timer) clearInterval(timer) })

function path(values: number[]): string {
  return sparkPathD(sparkPoints(values, W, H))
}

// JAPAN: today's synth since the last official NAV (rows sharing the latest anchor date).
const japanValues = computed(() => {
  const rows = intraday.value.filter(r => r.synthNavPence != null)
  const anchor = rows.length ? rows[rows.length - 1].anchorDate : null
  return rows.filter(r => r.anchorDate === anchor).map(r => r.synthNavPence as number)
})
const japanPct = computed(() => {
  const r = liveReturn.value ?? intraday.value.at(-1)?.returnFrac ?? null
  return r == null ? null : r * 100
})

const niyValues = computed(() => niy.value.map(b => b.close))
const niyPct = computed(() => {
  const last = niyValues.value.at(-1)
  return last != null && props.anchorPrice ? (last / props.anchorPrice - 1) * 100 : null
})
const niyMarkerX = computed(() => {
  if (niy.value.length < 2) return null
  const idx = findMarkerIndex(niy.value)
  return idx < 0 ? null : (idx / (niy.value.length - 1)) * W
})

const topixValues = computed(() =>
  topix.value.filter(r => r.topixClose != null).slice(-20).map(r => r.topixClose as number))
const topixPct = computed(() => {
  const v = topixValues.value
  return v.length >= 2 ? (v[v.length - 1] / v[v.length - 2] - 1) * 100 : null
})

const fxValues = computed(() => intraday.value.filter(r => r.gbpJpyMid != null).map(r => r.gbpJpyMid as number))
// GBP/JPY now vs the latest 12:00 fix before today (the fix the noon synth was last valued on).
const fxPct = computed(() => {
  const now = fxValues.value.at(-1)
  const today = new Date().toISOString().slice(0, 10)
  const fix = [...topix.value].reverse().find(r => r.date < today && r.gbpJpy1200 != null)?.gbpJpy1200
  return now != null && fix ? (now / fix - 1) * 100 : null
})

function fmtPct(v: number | null): string {
  if (v == null) return '—'
  return (v >= 0 ? '+' : '') + v.toFixed(2) + '%'
}
function returnClass(v: number | null): string {
  if (v == null) return ''
  return v >= 0 ? 'positive' : 'negative'
}
function goChart(p: string) {
  if (window.location.pathname === p) return
  window.history.pushState({}, '', p)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
</script>

<template>
  <div class="sparkline-bar" data-testid="japan-sparkline-bar">
    <div class="spark-item" data-testid="spark-japan" @click="goChart('/charts/japanvsniy')">
      <span class="spark-label">JAPAN</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <path v-if="japanValues.length > 1" :d="path(japanValues)" class="ln japan" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(japanPct)">{{ fmtPct(japanPct) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-niy" @click="goChart('/charts/japanvsniy')">
      <span class="spark-label niy">NIY</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <line v-if="niyMarkerX != null" data-testid="niy-marker-0840" :x1="niyMarkerX" :x2="niyMarkerX" y1="0" y2="18"
              class="marker" />
        <path v-if="niyValues.length > 1" :d="path(niyValues)" class="ln niy" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(niyPct)">{{ fmtPct(niyPct) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-topix" @click="goChart('/charts/japanvstopix')">
      <span class="spark-label topix">TOPIX</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <path v-if="topixValues.length > 1" :d="path(topixValues)" class="ln topix" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(topixPct)">{{ fmtPct(topixPct) }}</span>
    </div>

    <div class="spark-item" data-testid="spark-fx" @click="goChart('/charts/gbpjpy')">
      <span class="spark-label fx">FX</span>
      <svg viewBox="0 0 80 18" preserveAspectRatio="none" class="spark-svg">
        <path v-if="fxValues.length > 1" :d="path(fxValues)" class="ln fx" fill="none" />
      </svg>
      <span class="spark-value" :class="returnClass(fxPct)">{{ fmtPct(fxPct) }}</span>
    </div>
  </div>
</template>

<style scoped>
/* Mirrors SparklineBar.vue (BGA/JH strips) so the Japan strip reads identically. */
.sparkline-bar {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  gap: 16px;
  padding: 6px 12px;
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  margin-bottom: 8px;
}
.spark-item { display: flex; align-items: center; gap: 6px; min-width: 0; cursor: pointer; padding: 2px 4px; border-radius: 4px; }
.spark-item:hover { background: #161b22; }
.spark-svg { flex: 1 1 auto; min-width: 0; width: 100%; height: 18px; }
.spark-label { font-size: 10px; color: #4caf50; font-family: 'JetBrains Mono', monospace; min-width: 24px; flex: 0 0 auto; }
.spark-label.niy { color: #2196f3; }
.spark-label.topix { color: #ab47bc; }
.spark-label.fx { color: #26c6da; }
.spark-value { font-size: 10px; font-family: 'JetBrains Mono', monospace; color: #888; min-width: 40px; flex: 0 0 auto; }
.spark-value.positive { color: #4caf50; }
.spark-value.negative { color: #f44336; }
.ln { stroke-width: 1.2; vector-effect: non-scaling-stroke; }
.ln.japan { stroke: #4caf50; }
.ln.niy { stroke: #2196f3; }
.ln.topix { stroke: #ab47bc; }
.ln.fx { stroke: #26c6da; }
.marker { stroke: #8b949e; stroke-width: 1; stroke-dasharray: 2, 2; vector-effect: non-scaling-stroke; }

@media (max-width: 768px) {
  .sparkline-bar { grid-template-columns: repeat(2, 1fr); gap: 8px; padding: 4px 8px; }
  .spark-label { font-size: 9px; }
  .spark-value { font-size: 9px; }
}
</style>
